import { useVoyagerCRUDActions } from '@mm/state/actions/VoyagerCRUDActions';

import {
  VoyagerKeyType,
  VoyagerLeadsSearchQuery,
} from '@mm/state/interfaces/voyagerLeads';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

export const useVoyagerFilters = ({
  voyagerSearchQuery,
  fetchAttributes = false,
}: {
  voyagerSearchQuery: VoyagerLeadsSearchQuery;
  fetchAttributes?: boolean;
}) => {
  const [busy, setBusy] = useState(false);

  const isValueExists = useCallback(
    (key: VoyagerKeyType | undefined, value: string, operator?: string) => {
      if (!key && operator) {
        return !!voyagerSearchQuery.query.find(
          filter =>
            filter.operator === operator && filter.values?.includes(value),
        );
      }

      return !!voyagerSearchQuery.query?.find(
        filter => filter.field === key && filter.values?.includes(value),
      );
    },
    [voyagerSearchQuery.query],
  );

  const getUpdatedQuery = useCallback(
    (
      state: VoyagerLeadsSearchQuery,
      key: VoyagerKeyType | undefined,
      value: string,
      operator?: 'not_in' | 'search',
    ): VoyagerLeadsSearchQuery => {
      const { query = [] } = state || {};
      const isChecked = isValueExists(key, value, operator);

      const filterIndex = query.findIndex(
        item =>
          (key ? item.field === key : item.operator === operator) &&
          item.operator === operator,
      );

      const defaultParams = { field: key, ...(operator && { operator }) };
      if (filterIndex !== -1) {
        const filter = query[filterIndex];

        let updatedValues = isChecked
          ? filter.values.filter(v => v !== value)
          : [...(operator === 'search' ? [] : filter.values), value];

        if (operator === 'search') {
          updatedValues = updatedValues.map(v => v.trim()).filter(Boolean);
        }

        const updatedFilter = { ...filter, values: updatedValues };
        const updatedQuery = updatedValues.length
          ? query.map((item, index) =>
              index === filterIndex ? updatedFilter : item,
            )
          : query.filter((_, index) => index !== filterIndex);
        return { ...state, query: updatedQuery };
      }

      return {
        ...state,
        query: [...query, { ...defaultParams, values: [value] }],
      };
    },
    [isValueExists],
  );

  const { retrieveAttributes, searchLeads, resetLeads } =
    useVoyagerCRUDActions();

  useEffect(() => {
    (async () => {
      if (fetchAttributes) {
        setBusy(true);
        await retrieveAttributes();
        setBusy(false);
      }
    })();
  }, []);

  const debouncedLeadsSearch = useCallback(
    () => debounce(() => searchLeads(voyagerSearchQuery), 300),
    [voyagerSearchQuery, searchLeads],
  );

  useEffect(() => {
    if (voyagerSearchQuery?.query?.length) {
      debouncedLeadsSearch()();
    }
    if (!voyagerSearchQuery?.query?.length) {
      resetLeads();
    }
  }, [voyagerSearchQuery, debouncedLeadsSearch, resetLeads]);

  return {
    busy,
    isValueExists,
    getUpdatedQuery,
  };
};
